import { batch } from 'react-redux';

// project imports
import gestopAPI from 'utils/gestopAPI';
import { dispatch } from 'store/index';
import { errorProcessor } from 'utils/processors';
import { workOrderTypeActions } from './slice';
import capabilities from './capabilities';
import createImportApi from 'utils/importAPI';

const workOrderTypeImportApi = createImportApi({
    actions: workOrderTypeActions,
    capabilities,
    endpoint: 'workmgmt/workordertypes',
    templateFileName: 'WorkOrderTypeImportTpl'
});
export const ApiWorkOrderTypesImportCollection = workOrderTypeImportApi.importCollection;
export const ApiWorkOrderTypesDownloadImportModelItem = workOrderTypeImportApi.downloadImportModel;

export const ApiWorkOrderTypesGetCollection = async ({
    filters = [],
    orderBy = [],
    groupBy = [],
    page = 1,
    limit = 10,
    includeSafeDeleted = false,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (updateStore && capabilities.app.includes('loader')) {
        dispatch(workOrderTypeActions.startLoading());
    }

    await gestopAPI
        .post('workmgmt/workordertypes/list', { filters, orderBy, groupBy, page, limit, includeSafeDeleted }, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(workOrderTypeActions.setList(response?.data?.list));
                    dispatch(workOrderTypeActions.setCount(response?.data?.count));
                }
                if (updateStore && capabilities.app.includes('loader')) {
                    dispatch(workOrderTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data?.list);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (updateStore && capabilities.app.includes('loader')) {
                    dispatch(workOrderTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(workOrderTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiWorkOrderTypesCountCollection = async ({
    filters = [],
    includeSafeDeleted = false,
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    await gestopAPI
        .post('workmgmt/workordertypes/count', { filters, includeSafeDeleted }, config)
        .then((response) => {
            if (typeof successCallback === 'function') {
                successCallback(response?.data?.count);
            }
        })
        .catch((error) => {
            const errorMsg = errorProcessor(error?.response?.data);

            if (typeof errorCallback === 'function') {
                errorCallback(errorMsg);
            }
        });
};

export const ApiWorkOrderTypesPostCollection = async ({
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(workOrderTypeActions.startLoading());
    }

    await gestopAPI
        .post('workmgmt/workordertypes', data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(workOrderTypeActions.insertItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(workOrderTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(workOrderTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(workOrderTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiWorkOrderTypesDeleteCollection = async ({
    ids = [],
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(workOrderTypeActions.startLoading());
    }

    await gestopAPI
        .post('workmgmt/workordertypes/safedelete', ids, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(workOrderTypeActions.deleteItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(workOrderTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(workOrderTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(workOrderTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiWorkOrderTypesExportCollection = async ({
    exportType = 'allRows',
    format = 'pdf',
    columns = [],
    filters = [],
    orderBy = [],
    groupBy = [],
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    if (capabilities.app.includes('exporter')) {
        dispatch(workOrderTypeActions.startExporting());
    }

    await gestopAPI
        .post(
            'workmgmt/workordertypes/export',
            { exportType, format, columns, filters, orderBy, groupBy },
            { ...config, responseType: 'blob' }
        )
        .then((response) =>
            batch(() => {
                window.open(URL.createObjectURL(response?.data));

                if (capabilities.app.includes('exporter')) {
                    dispatch(workOrderTypeActions.stopExporting());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('exporter')) {
                    dispatch(workOrderTypeActions.stopExporting());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(workOrderTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiWorkOrderTypesPutItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(workOrderTypeActions.startLoading());
    }

    await gestopAPI
        .put(`workmgmt/workordertypes/${id}`, data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(workOrderTypeActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(workOrderTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(workOrderTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(workOrderTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiWorkOrderTypesPatchItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(workOrderTypeActions.startLoading());
    }

    await gestopAPI
        .patch(`workmgmt/workordertypes/${id}`, data, {
            ...config,
            headers: { ...config?.headers, 'Content-Type': 'application/merge-patch+json' } // Specific content type for PATCH requests
        })
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(workOrderTypeActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(workOrderTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(workOrderTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(workOrderTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiWorkOrderTypesDeleteItem = async ({
    id,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(workOrderTypeActions.startLoading());
    }

    await gestopAPI
        .post(`workmgmt/workordertypes/safedelete/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(workOrderTypeActions.deleteItems(id));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(workOrderTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(workOrderTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(workOrderTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiWorkOrderTypesRestoreDeletedItem = async ({
    id,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(workOrderTypeActions.startLoading());
    }

    await gestopAPI
        .post(`workmgmt/workordertypes/restoresafedeleted/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(workOrderTypeActions.deleteItems(id));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(workOrderTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(workOrderTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(workOrderTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiWorkOrderTypesGetItem = async ({ id, successCallback = () => {}, errorCallback = () => {}, config = {} }) => {
    if (capabilities.app.includes('loader')) {
        dispatch(workOrderTypeActions.startLoading());
    }

    await gestopAPI
        .get(`workmgmt/workordertypes/${id}`, config)
        .then((response) =>
            batch(() => {
                if (capabilities.app.includes('loader')) {
                    dispatch(workOrderTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(workOrderTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(workOrderTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};
